import { ReasonOptionKind } from '@prisma/client';
import { z } from 'zod';

export type ReasonOptionChoice = { id: number; name: string; requiresFreeText: boolean };

/** The consumer relations `ReasonOptionService.list` counts. One per model that can point at a reason. */
export type ReasonOptionUsageKey = 'estimates' | 'jobs' | 'leads';

/**
 * Which entitlement a list sits behind. A token rather than a `Prisma.BusinessWhereInput[]` because it has
 * two consumers in two layers — the router turns it into a `where`, `visibleReasonOptionKinds` below turns it
 * into a boolean — and a where-fragment has no business being imported into client code. Each side resolves it
 * with its own total `Record<ReasonOptionEntitlement, …>`, so adding a token here breaks both until they
 * say what it means.
 */
export type ReasonOptionEntitlement = 'ALWAYS' | 'LEADS';

type ReasonOptionKindMeta = {
	/** Sentence under the heading on /settings/reasons, explaining when this list is offered. */
	description: string;
	entitlement: ReasonOptionEntitlement;
	heading: string;
	/**
	 * Settings-hub search vocabulary — synonyms and the words people actually type. Never shown. Lives here
	 * so a new list becomes findable without editing the Reasons card in `pages/settings/index.tsx`.
	 */
	keywords: string;
	pickerLabel: string;
	/**
	 * The consumers whose counts this list shows, in column order. Drives both the settings grid's columns
	 * and its delete-confirmation sentence, so a kind cannot show a column of zeroes for a consumer that can
	 * never reference it.
	 */
	usage: { columnHeader: string; key: ReasonOptionUsageKey; many: string; one: string }[];
};

/**
 * Everything per-list that is not stored in the database: the copy, which consumers it has, and what it is
 * sold behind. Total over `ReasonOptionKind`, so a new list is a compile error until all of it is filled in —
 * which is what lets /settings/reasons render every kind generically instead of growing a page each time.
 */
export const REASON_OPTION_KIND_LABELS: Record<ReasonOptionKind, ReasonOptionKindMeta> = {
	LEAD_DISQUALIFIED: {
		description:
			'Offered when a lead is disqualified. Once you add one, picking a reason becomes required — add a reason with "Asks for details" if your team still needs a free-text option.',
		entitlement: 'LEADS',
		heading: 'Lead Disqualification Reasons',
		keywords: 'lead leads disqualified unqualified rejected lost',
		pickerLabel: 'Reason',
		usage: [{ columnHeader: 'Leads', key: 'leads', many: 'leads', one: 'lead' }],
	},
	LOST_WORK: {
		description:
			'Offered when a job is marked lost and when an estimate or change order is rejected. Once you add one, picking a reason becomes required.',
		entitlement: 'ALWAYS',
		heading: 'Lost & Rejected Reasons',
		keywords: 'lost lose job estimate change order rejected why did we lose',
		pickerLabel: 'Reason',
		usage: [
			{ columnHeader: 'Jobs', key: 'jobs', many: 'jobs', one: 'job' },
			{ columnHeader: 'Estimates', key: 'estimates', many: 'estimates', one: 'estimate' },
		],
	},
};

/**
 * The lists a business may see, in `ReasonOptionKind` declaration order. The client half of each entitlement
 * token, total so a new token cannot be added without deciding whether it shows; the server half is
 * `ENTITLEMENT_FILTERS` in `reasonOption.router.ts`.
 */
export const visibleReasonOptionKinds = ({ isLeadsEnabled }: { isLeadsEnabled: boolean }) => {
	const isEntitled: Record<ReasonOptionEntitlement, boolean> = {
		ALWAYS: true,
		LEADS: isLeadsEnabled,
	};

	return Object.values(ReasonOptionKind).filter((kind) => isEntitled[REASON_OPTION_KIND_LABELS[kind].entitlement]);
};

export class ReasonOptionSchema {
	/**
	 * Shape only, and deliberately carries no `.refine`: which combination is legal depends on the picked
	 * row's `requiresFreeText` flag and on whether the business has configured any reasons at all, both of
	 * which are database reads. A `requiresFreeText` row legitimately sends both fields. The rules live in
	 * `ReasonOptionService.resolve`, which every write goes through.
	 */
	static reasonInputSchema = z.object({
		reasonId: z.number().nullable().default(null),
		reasonText: z.string().trim().min(1).nullable().default(null),
	});

	static upsertSchema = z.object({
		name: z.string().trim().min(1, 'Name is required').max(100),
		requiresFreeText: z.boolean().default(false),
	});
}

export type ReasonInput = z.infer<typeof ReasonOptionSchema.reasonInputSchema>;

/**
 * The one place a reason becomes display text — job log sentences, both reports, the estimate cards. Guards
 * the two states `requiresFreeText` allows after the fact, since flipping the flag does not rewrite existing
 * records: a flag turned on leaves rows with an FK and no note, one turned off leaves rows with both. Neither
 * may render a trailing separator.
 */
export const formatReasonOption = ({ name, text }: { name?: string | null; text?: string | null }) => {
	const trimmedName = name?.trim() || null;
	const trimmedText = text?.trim() || null;

	if (trimmedName && trimmedText) return `${trimmedName} — ${trimmedText}`;

	return trimmedName ?? trimmedText;
};
